import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Drawer,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { navItems, NAV_ACTIONS } from "./utils/navigationSettings";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PolicyIcon from "@mui/icons-material/Policy";
import SearchIcon from "@mui/icons-material/Search";
import SchedulesMenu from "./SchedulesList/ScheduleMenu";
import CASchedulesMenu from "./SchedulesList/CAScheduleMenu";
import {
  APP_LAYOUT_WIDTHS,
  shouldAutoCollapseNavigation,
} from "../../utils/responsiveLayoutSettings";

const getScheduleSearchText = (schedule = {}) =>
  [
    schedule.scheduleTitle,
    schedule.scheduleNo,
    schedule.scheduleType,
    schedule.name,
    schedule.type,
    schedule.sch_no,
    schedule.schNo,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const filterSchedulesBySearch = (items = [], searchValue = "") => {
  const query = searchValue.trim().toLowerCase();
  if (!query) return items || [];

  return (items || []).filter((schedule) =>
    getScheduleSearchText(schedule).includes(query)
  );
};

const NavigationPanel = ({
  company,
  open,
  tabs,
  onNavClick,
  onToggleDrawer,
  schedules,
  CASchedules,
  isFetchingSchedules,
  isFetchingCASchedules,
  schMenuActions,
  onOpenDisclosureLibrary,
}) => {
  const navigate = useNavigate();

  const drawerWidth = APP_LAYOUT_WIDTHS.navigationDrawer;
  const collapsedWidth = APP_LAYOUT_WIDTHS.navigationDrawerCollapsed;

  // 'pl' | 'ca' | null
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [lastActiveSubmenu, setLastActiveSubmenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const normalisedSearchQuery = searchQuery.trim().toLowerCase();
  const isSearching = normalisedSearchQuery.length > 0;

  const filteredSchedules = useMemo(
    () => filterSchedulesBySearch(schedules, normalisedSearchQuery),
    [schedules, normalisedSearchQuery]
  );
  const filteredCASchedules = useMemo(
    () => filterSchedulesBySearch(CASchedules, normalisedSearchQuery),
    [CASchedules, normalisedSearchQuery]
  );

  const searchMatchesPlSchedules = filteredSchedules.length > 0;
  const searchMatchesCASchedules = filteredCASchedules.length > 0;

  const visibleNavItems = useMemo(
    () =>
      navItems
        .filter((item) => item.show(company))
        .filter((item) => {
          if (!isSearching) return true;

          const itemMatches = item.label
            .toLowerCase()
            .includes(normalisedSearchQuery);
          if (item.submenuKey === "pl") {
            return itemMatches || searchMatchesPlSchedules;
          }
          if (item.submenuKey === "ca") {
            return itemMatches || searchMatchesCASchedules;
          }

          return itemMatches;
        }),
    [
      company,
      isSearching,
      normalisedSearchQuery,
      searchMatchesCASchedules,
      searchMatchesPlSchedules,
    ]
  );

  const toggleSubmenu = (key) => {
    setActiveSubmenu((current) => {
      const next = current === key ? null : key;
      if (next) {
        setLastActiveSubmenu(next);
      }
      return next;
    });
  };

  const handleDrawerToggle = () => {
    if (open) {
      // remember current submenu and close it when collapsing
      setLastActiveSubmenu(activeSubmenu);
      setActiveSubmenu(null);
    } else if (lastActiveSubmenu) {
      // restore last visited submenu when re‑expanding
      setActiveSubmenu(lastActiveSubmenu);
    }
    onToggleDrawer();
  };

  useEffect(() => {
    if (!open || !isSearching) return;

    if (
      (activeSubmenu === "pl" && searchMatchesPlSchedules) ||
      (activeSubmenu === "ca" && searchMatchesCASchedules)
    ) {
      return;
    }

    const nextSubmenu = searchMatchesPlSchedules
      ? "pl"
      : searchMatchesCASchedules
        ? "ca"
        : null;

    setActiveSubmenu(nextSubmenu);
    if (nextSubmenu) setLastActiveSubmenu(nextSubmenu);
  }, [
    activeSubmenu,
    isSearching,
    open,
    searchMatchesCASchedules,
    searchMatchesPlSchedules,
  ]);

  const shouldAutoCollapseForItem = (item) => {
    return shouldAutoCollapseNavigation({
      open,
      sectionKey: item.sectionKey,
    });
  };

  const isItemActive = (item) =>
    tabs.some(
      (tab) =>
        tab.label === item.label &&
        String(tab.sectionKey || "") === String(item.sectionKey || "")
    );

  const listItemBaseSx = useMemo(
    () => ({
      py: 1.2,
      px: 2,
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 6,
        bottom: 6,
        width: "3px",
        borderRadius: "0 4px 4px 0",
        bgcolor: "transparent",
        transition: "background-color 0.2s ease-out",
      },
      "&:hover": {
        bgcolor: "primary.lighter",
        transform: "translateX(4px)",
      },
      transition: "transform 0.2s ease-out, background-color 0.2s ease-out",
    }),
    []
  );

  const handleBack = () => {
    navigate(`/company`, { state: { company } });
  };

  const hasSearchResults =
    !isSearching ||
    visibleNavItems.length > 0 ||
    filteredSchedules.length > 0 ||
    filteredCASchedules.length > 0;

  return (
    <Drawer
      variant='permanent'
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        position: "relative",
        transition: "width 0.3s ease-in-out",
        "& .MuiDrawer-paper": {
          width: open ? drawerWidth : collapsedWidth,
          transition: "width 0.3s ease-in-out, opacity 0.2s ease-in-out",
          overflow: "hidden",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          marginTop: "70px",
          height: "calc(100vh - 70px)",
          bgcolor: "background.default",
          borderRight: 1,
          borderColor: "divider",
          opacity: open ? 1 : 0.95,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.2)",
            borderRadius: "4px",
            "&:hover": {
              background: "rgba(0, 0, 0, 0.3)",
            },
          },
        },
      }}
      aria-label='Navigation drawer'
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Sticky top controls */}
        <Box
          sx={{
            alignItems: open ? "stretch" : "center",
            display: "flex",
            flexDirection: "column",
            gap: open ? 1 : 0,
            p: 1,
            width: open ? drawerWidth : collapsedWidth,
            transition: "width 0.3s ease-in-out",
            zIndex: 1,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            backdropFilter: "blur(8px)",
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: open ? "row" : "column",
              gap: 0.75,
              justifyContent: open ? "space-between" : "center",
              minHeight: 40,
            }}
          >
            {open && (
              <ListItem
                button
                onClick={handleBack}
                sx={{
                  borderRadius: 1,
                  flex: 1,
                  minWidth: 0,
                  px: 1,
                  py: 0.5,
                  "&:hover": {
                    bgcolor: "primary.lighter",
                    "& .MuiListItemIcon-root": {
                      transform: "translateX(-2px)",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "primary.main",
                    minWidth: 32,
                    transition: "transform 0.2s ease",
                  }}
                >
                  <ArrowBackIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText
                  primary='Back'
                  primaryTypographyProps={{
                    color: "text.primary",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                />
              </ListItem>
            )}
            {!open && (
              <Tooltip title='Expand' placement='right'>
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{
                    color: "primary.main",
                    flexShrink: 0,
                    height: 36,
                    width: 36,
                    "&:hover": {
                      bgcolor: "primary.lighter",
                    },
                  }}
                  aria-label='Expand drawer'
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}
            {!open && (
              <Tooltip title='Back' placement='right'>
                <IconButton
                  onClick={handleBack}
                  sx={{
                    color: "primary.main",
                    flexShrink: 0,
                    height: 36,
                    width: 36,
                    "&:hover": {
                      bgcolor: "primary.lighter",
                      transform: "translateX(-2px)",
                    },
                  }}
                  aria-label='Back'
                >
                  <ArrowBackIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {open && (
              <Tooltip title='Collapse' placement='right'>
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{
                    color: "primary.main",
                    flexShrink: 0,
                    height: 36,
                    width: 36,
                    "&:hover": {
                      bgcolor: "primary.lighter",
                    },
                  }}
                  aria-label='Collapse drawer'
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {open && (
            <TextField
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder='Search schedules'
              size='small'
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='Clear navigation search'
                      edge='end'
                      onClick={() => setSearchQuery("")}
                      size='small'
                      sx={{ mr: -0.5 }}
                    >
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiInputBase-root": {
                  bgcolor: "background.default",
                  borderRadius: 1,
                  fontSize: "0.875rem",
                },
              }}
            />
          )}
        </Box>

        {/* Navigation List (scrollable) */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0, 0, 0, 0.2)",
              borderRadius: "4px",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.3)",
              },
            },
          }}
        >
          <List
            sx={{
              pb: 2,
              px: 1,
              flex: 1,
              "& .MuiListItem-root": {
                mb: 0.5,
                borderRadius: 1,
                transition: "all 0.2s ease-in-out",
              },
            }}
          >
            {visibleNavItems.map((item, index) => (
              <React.Fragment key={index}>
                <Tooltip title={open ? "" : item.label} placement='right'>
                  <ListItem
                    button
                    onClick={() => {
                      if (item.action === NAV_ACTIONS.TOGGLE_SUBMENU) {
                        // Collapsed behaviour: keep drawer collapsed but allow a quick peek via submenu toggle
                        if (open) {
                          if (item.submenuKey === "pl") {
                            toggleSubmenu("pl");
                          } else if (item.submenuKey === "ca") {
                            toggleSubmenu("ca");
                          }
                        } else {
                          // Collapsed: expand drawer + open corresponding submenu in one smooth action
                          if (!open) {
                            if (item.submenuKey === "pl") {
                              setLastActiveSubmenu("pl");
                            } else if (item.submenuKey === "ca") {
                              setLastActiveSubmenu("ca");
                            }
                            handleDrawerToggle();
                          }
                        }
                      } else {
                        onNavClick(
                          item,
                          () => toggleSubmenu("pl"),
                          () => toggleSubmenu("ca")
                        );
                        if (shouldAutoCollapseForItem(item)) {
                          handleDrawerToggle();
                        }
                      }
                    }}
                    sx={{
                      ...listItemBaseSx,
                      bgcolor: isItemActive(item)
                        ? "primary.lighter"
                        : "transparent",
                      "&::before": {
                        ...listItemBaseSx["&::before"],
                        bgcolor: isItemActive(item)
                          ? "primary.main"
                          : "transparent",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "36px",
                        color: isItemActive(item)
                          ? "primary.main"
                          : "text.secondary",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: isItemActive(item) ? 600 : 400,
                          color: isItemActive(item)
                            ? "primary.main"
                            : "text.primary",
                        }}
                      />
                    )}
                    {["P&L Schedules", "CA Schedules"].includes(item.label) &&
                      open && (
                        <Box
                          component='span'
                          sx={{
                            transition: "transform 0.2s ease",
                            transform:
                              (item.label === "P&L Schedules" &&
                                activeSubmenu === "pl") ||
                              (item.label === "CA Schedules" &&
                                activeSubmenu === "ca")
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                          }}
                        >
                          <ExpandMoreIcon
                            fontSize='small'
                            sx={{ color: "text.secondary" }}
                          />
                        </Box>
                      )}
                  </ListItem>
                </Tooltip>

                {/* Submenu Collapses */}
                {(item.label === "P&L Schedules" ||
                  item.label === "CA Schedules") && (
                  <Collapse
                    in={
                      open &&
                      ((item.label === "P&L Schedules" &&
                        activeSubmenu === "pl") ||
                        (item.label === "CA Schedules" &&
                          activeSubmenu === "ca"))
                    }
                    timeout='auto'
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        py: isSearching ? 0.5 : 1,
                        maxHeight: "40vh",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": {
                          width: 6,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "grey.400",
                          borderRadius: 3,
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          backgroundColor: "grey.500",
                        },
                      }}
                    >
                      {item.label === "P&L Schedules" ? (
                        <SchedulesMenu
                          schedules={filteredSchedules}
                          allSchedules={schedules}
                          isFetching={isFetchingSchedules}
                          searchMode={isSearching}
                          onAddSchedule={schMenuActions.onCreatePlSchedule}
                          onOpenSchedule={(schedule, scheduleType) =>
                            schMenuActions.onOpenSchedule(
                              "P&L",
                              schedule,
                              scheduleType
                            )
                          }
                          reorderSchedule={schMenuActions.reorderSchedule}
                          moveSchedule={(dragIndex, hoverIndex) => {
                            schMenuActions.moveSchedule(
                              "P&L",
                              dragIndex,
                              hoverIndex
                            );
                          }}
                        />
                      ) : (
                        <CASchedulesMenu
                          schedules={filteredCASchedules}
                          allSchedules={CASchedules}
                          isFetching={isFetchingCASchedules}
                          searchMode={isSearching}
                          onAddSchedule={schMenuActions.onCreateCaSchedule}
                          onOpenSchedule={(schedule, scheduleType) =>
                            schMenuActions.onOpenSchedule(
                              "CA",
                              schedule,
                              scheduleType
                            )
                          }
                          reorderSchedule={schMenuActions.reorderCASchedule}
                          moveSchedule={(dragIndex, hoverIndex) => {
                            schMenuActions.moveSchedule(
                              "CA",
                              dragIndex,
                              hoverIndex
                            );
                          }}
                        />
                      )}
                    </Box>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
            {!hasSearchResults && open && (
              <Box
                sx={{
                  px: 2,
                  py: 3,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ color: "text.primary", fontWeight: 600, mb: 0.5 }}
                >
                  No matches found
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Try a schedule name, type, or number.
                </Typography>
              </Box>
            )}
          </List>
        </Box>

        {/* Disclosure library — pinned to bottom */}
        <Box
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            px: 1,
            pt: 0.5,
            pb: 1,
          }}
        >
          <Tooltip
            title='Disclosure Library (Shortcuts: Ctrl+/ or Ctrl+Shift+L)'
            placement='right'
          >
            <ListItem
              button
              onClick={() => onOpenDisclosureLibrary?.()}
              sx={{
                ...listItemBaseSx,
                borderRadius: 1,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: "36px",
                  color: "primary.main",
                }}
              >
                <PolicyIcon fontSize='small' />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary='Disclosure Library'
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "text.primary",
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default NavigationPanel;
